<template>
  <section class="split">
    <div class="panel picker">
      <div class="panel-head">
        <h2>厂商目录</h2>
        <div class="head-actions">
          <button type="button" class="btn ghost sm" @click="startCreateVendor">+ 厂商</button>
          <button type="button" class="btn primary sm" @click="startCreate()">+ 算力卡</button>
        </div>
      </div>
      <div class="search-row">
        <input v-model="keyword" placeholder="搜索厂商或卡名" @keyup.enter="loadTree" />
        <button type="button" class="btn ghost sm" @click="loadTree">搜索</button>
      </div>
      <div class="link-row">
        <button type="button" class="linkish" @click="reclassifyVendors">按规则重分类</button>
        <button type="button" class="linkish" @click="rebuildMetrics">重建规格索引</button>
      </div>
      <p v-if="listError" class="error">{{ listError }}</p>
      <p v-if="saveMessage && !current && !creating" class="ok">{{ saveMessage }}</p>

      <div v-if="vendorForm.open" class="inline-form">
        <input
          v-model="vendorForm.name"
          :placeholder="vendorForm.id ? '厂商新名称' : '厂商名称，如 NVIDIA'"
          @keyup.enter="saveVendor"
        />
        <button type="button" class="btn primary sm" @click="saveVendor">{{ vendorForm.id ? '保存' : '创建' }}</button>
        <button type="button" class="btn ghost sm" @click="cancelVendorForm">取消</button>
      </div>

      <ul class="tree">
        <li v-for="vendor in tree" :key="vendor.id + '-' + vendor.name" class="vendor-node">
          <div class="vendor-row" :class="{ on: selectedVendorName === vendor.name && !current }">
            <button type="button" class="twist" @click="toggleVendor(vendor.name)">
              {{ expanded[vendor.name] ? '▾' : '▸' }}
            </button>
            <button type="button" class="vendor-name" @click="selectVendor(vendor)">
              {{ vendor.name }}
              <span class="count">{{ vendor.card_count }}</span>
            </button>
            <span class="vendor-ops">
              <template v-if="!vendor.virtual">
                <button type="button" class="mini" @click="startRenameVendor(vendor)">改</button>
                <button type="button" class="mini" @click="startCreate(vendor.name)">+</button>
                <button type="button" class="mini danger" @click="removeVendor(vendor)">删</button>
              </template>
              <button v-else type="button" class="mini" @click="startCreate('')">+</button>
            </span>
          </div>
          <ul v-if="expanded[vendor.name]" class="card-nodes">
            <li v-for="item in vendor.cards" :key="item.id">
              <button
                type="button"
                class="card-name"
                :class="{ on: current && current.id === item.id }"
                @click="openCard(item.id)"
              >
                {{ item.name }}
                <span v-if="!item.published" class="off">隐藏</span>
              </button>
            </li>
            <li v-if="!vendor.cards.length" class="empty-node">暂无卡片，点「+」新增</li>
          </ul>
        </li>
      </ul>
    </div>

    <div class="panel editor">
      <div v-if="creating" class="create">
        <div class="panel-head">
          <h2>新增算力卡</h2>
        </div>
        <label>
          <span>名称</span>
          <input v-model="draftName" placeholder="例如 NVIDIA H200" />
        </label>
        <label>
          <span>厂商</span>
          <select v-model="draftVendor">
            <option value="">未分类</option>
            <option v-for="vendor in realVendors" :key="vendor.id" :value="vendor.name">{{ vendor.name }}</option>
          </select>
        </label>
        <div class="row">
          <button type="button" class="btn primary" @click="createCard">创建</button>
          <button type="button" class="btn ghost" @click="cancelCreate">取消</button>
        </div>
        <p v-if="saveMessage" class="ok">{{ saveMessage }}</p>
      </div>

      <template v-else-if="current">
        <div class="panel-head">
          <h2>编辑卡片</h2>
          <span class="chip" :class="{ off: !current.published }">{{ current.published ? '对外展示' : '已隐藏' }}</span>
        </div>
        <div class="form-grid">
          <label>
            <span>名称</span>
            <input v-model="current.name" />
          </label>
          <label>
            <span>厂商</span>
            <select v-model="current.vendor">
              <option value="">未分类</option>
              <option v-for="vendor in realVendors" :key="vendor.id" :value="vendor.name">{{ vendor.name }}</option>
            </select>
          </label>
        </div>
        <label class="check">
          <input v-model="current.published" type="checkbox" />
          对外展示
        </label>
        <div class="row">
          <button type="button" class="btn primary" @click="saveCard">保存卡片</button>
          <button type="button" class="btn danger" @click="removeCard">删除</button>
        </div>
        <p v-if="saveMessage" class="ok">{{ saveMessage }}</p>

        <h3>结构化索引</h3>
        <div v-if="metrics" class="metrics">
          <label class="check"><input v-model="metrics.is_domestic" type="checkbox" /> 国产</label>
          <input v-model.number="metrics.memory_gb" type="number" placeholder="显存 GB" />
          <input v-model.number="metrics.fp16_tflops" type="number" placeholder="FP16 T" />
          <input v-model.number="metrics.fp8_tflops" type="number" placeholder="FP8 T" />
          <label class="check"><input v-model="metrics.locked" type="checkbox" /> 锁定</label>
          <button type="button" class="btn ghost sm" @click="saveMetrics">保存索引</button>
        </div>

        <h3>参数字段</h3>
        <div v-for="attr in current.attrs" :key="attr.id" class="attr-row">
          <input v-model="attr.field_label" placeholder="字段名" />
          <input v-model="attr.field_value" placeholder="字段值" />
          <button type="button" class="btn ghost sm" @click="saveAttr(attr)">保存</button>
          <button type="button" class="btn ghost sm" @click="removeAttr(attr)">删</button>
        </div>
        <div class="attr-row add">
          <input v-model="newAttr.field_label" placeholder="新字段名" />
          <input v-model="newAttr.field_value" placeholder="新字段值" />
          <button type="button" class="btn primary sm" @click="addAttr">添加</button>
        </div>

        <h3>卡价库</h3>
        <div v-for="price in current.prices" :key="price.id" class="price-block">
          <div class="attr-row price">
            <input v-model="price.price_month" type="month" />
            <input v-model="price.observed_at_local" type="datetime-local" />
            <input v-model="price.variant_name" placeholder="规格名" />
            <input v-model="price.msrp" placeholder="官方价" />
            <input v-model="price.market_price" placeholder="市场价" />
            <input v-model="price.secondhand_price" placeholder="二手价" />
            <input v-model="price.currency" placeholder="USD / CNY" />
          </div>
          <div class="attr-row price">
            <input v-model="price.source_label" placeholder="来源名称" />
            <input v-model="price.source_url" placeholder="来源链接 https://…" />
            <button type="button" class="btn ghost sm" @click="savePrice(price)">保存</button>
            <button type="button" class="btn ghost sm" @click="removePrice(price)">删</button>
          </div>
        </div>
        <div class="price-block add">
          <div class="attr-row price">
            <input v-model="newPrice.price_month" type="month" />
            <input v-model="newPrice.observed_at_local" type="datetime-local" />
            <input v-model="newPrice.variant_name" placeholder="规格名" />
            <input v-model="newPrice.msrp" placeholder="官方价" />
            <input v-model="newPrice.market_price" placeholder="市场价" />
            <input v-model="newPrice.secondhand_price" placeholder="二手价" />
            <input v-model="newPrice.currency" placeholder="USD / CNY" />
          </div>
          <div class="attr-row price">
            <input v-model="newPrice.source_label" placeholder="来源名称，如 京东 / 闲鱼 / 官网" />
            <input v-model="newPrice.source_url" placeholder="来源链接 https://…" />
            <button type="button" class="btn primary sm" @click="addPrice">添加价格</button>
          </div>
        </div>
      </template>

      <div v-else class="placeholder">
        <h2>{{ selectedVendorName ? `厂商：${selectedVendorName}` : '选择厂商或卡片' }}</h2>
        <p>左侧按厂商展开目录。可新增/改名/删除厂商，或在厂商下新增卡片后编辑参数与价格。</p>
      </div>
    </div>
  </section>
</template>

<script>
import {
  adminAddAttr,
  adminAddPrice,
  adminCreateCard,
  adminCreateVendor,
  adminDeleteAttr,
  adminDeleteCard,
  adminDeletePrice,
  adminDeleteVendor,
  adminGetCard,
  adminUpdateAttr,
  adminUpdateCard,
  adminUpdatePrice,
  adminUpdateVendor,
  adminVendorTree
} from '../../api/accelService'
import { getCardMetrics, putCardMetrics, rebuildMetrics, reclassifyVendors as reclassifyVendorsApi } from '../../api/platformService'
import { currentMonth, emptyPriceDraft, fromLocalDatetime, hydratePrices, toLocalDatetime } from './priceHelpers'

export default {
  name: 'AdminCardCatalog',
  emits: ['unauthorized'],
  data() {
    return {
      keyword: '',
      tree: [],
      expanded: {},
      selectedVendorName: '',
      current: null,
      creating: false,
      draftName: '',
      draftVendor: '',
      vendorForm: { open: false, id: null, name: '' },
      listError: '',
      saveMessage: '',
      newAttr: { field_label: '', field_value: '' },
      newPrice: emptyPriceDraft(),
      metrics: null
    }
  },
  computed: {
    realVendors() {
      return this.tree.filter((item) => !item.virtual)
    }
  },
  mounted() {
    this.loadTree()
  },
  methods: {
    async openFromGap(id) {
      await this.loadTree()
      await this.openCard(id)
    },
    async loadTree() {
      this.listError = ''
      try {
        const prevExpanded = { ...this.expanded }
        this.tree = await adminVendorTree({ q: this.keyword })
        const next = {}
        this.tree.forEach((vendor) => {
          const keep = prevExpanded[vendor.name]
          const hitSearch = Boolean(this.keyword)
          const selected = vendor.name === this.selectedVendorName
            || vendor.name === (this.current?.vendor || (this.current ? '未分类' : ''))
          next[vendor.name] = keep ?? (hitSearch || selected)
        })
        this.expanded = next
      } catch (err) {
        const status = err.response?.status
        if (status === 401 || status === 403) this.$emit('unauthorized')
        this.listError = err.response?.data?.error || '加载失败'
      }
    },
    toggleVendor(name) {
      this.expanded = { ...this.expanded, [name]: !this.expanded[name] }
    },
    selectVendor(vendor) {
      this.selectedVendorName = vendor.name
      this.expanded = { ...this.expanded, [vendor.name]: true }
      this.creating = false
      this.current = null
      this.saveMessage = ''
    },
    startCreateVendor() {
      this.vendorForm = { open: true, id: null, name: '' }
    },
    startRenameVendor(vendor) {
      this.vendorForm = { open: true, id: vendor.id, name: vendor.name }
      this.expanded = { ...this.expanded, [vendor.name]: true }
    },
    cancelVendorForm() {
      this.vendorForm = { open: false, id: null, name: '' }
    },
    async saveVendor() {
      const name = this.vendorForm.name.trim()
      if (!name) {
        this.listError = '请填写厂商名称'
        return
      }
      this.listError = ''
      try {
        if (this.vendorForm.id) await adminUpdateVendor(this.vendorForm.id, { name })
        else await adminCreateVendor({ name })
        this.selectedVendorName = name
        this.cancelVendorForm()
        await this.loadTree()
        this.expanded = { ...this.expanded, [name]: true }
        this.saveMessage = '厂商已保存'
      } catch (err) {
        this.listError = err.response?.data?.error || '厂商保存失败'
      }
    },
    async removeVendor(vendor) {
      if (vendor.virtual) return
      if (!window.confirm(`删除厂商「${vendor.name}」？仅在没有卡片时可删。`)) return
      try {
        await adminDeleteVendor(vendor.id)
        if (this.selectedVendorName === vendor.name) this.selectedVendorName = ''
        await this.loadTree()
      } catch (err) {
        this.listError = err.response?.data?.error || '删除失败'
      }
    },
    startCreate(vendorName) {
      this.creating = true
      this.current = null
      this.metrics = null
      this.draftName = ''
      this.draftVendor = vendorName || this.selectedVendorName || ''
      this.saveMessage = ''
      if (this.draftVendor) this.expanded = { ...this.expanded, [this.draftVendor]: true }
    },
    cancelCreate() {
      this.creating = false
      this.draftName = ''
      this.draftVendor = ''
    },
    async createCard() {
      if (!this.draftName.trim()) {
        this.saveMessage = ''
        this.listError = '请填写卡片名称'
        return
      }
      try {
        const created = await adminCreateCard({
          name: this.draftName.trim(),
          vendor: this.draftVendor || '',
          published: false
        })
        this.creating = false
        await this.loadTree()
        await this.openCard(created.id)
        this.saveMessage = '已创建'
      } catch (err) {
        this.listError = err.response?.data?.error || '创建失败'
      }
    },
    async openCard(id) {
      this.creating = false
      this.saveMessage = ''
      this.current = hydratePrices(await adminGetCard(id))
      this.selectedVendorName = this.current.vendor || '未分类'
      this.expanded = { ...this.expanded, [this.selectedVendorName]: true }
      try {
        this.metrics = await getCardMetrics(id)
      } catch {
        this.metrics = {
          is_domestic: false,
          memory_gb: null,
          fp16_tflops: null,
          fp8_tflops: null,
          locked: false
        }
      }
    },
    async saveCard() {
      const saved = await adminUpdateCard(this.current.id, {
        name: this.current.name,
        vendor: this.current.vendor,
        published: this.current.published
      })
      Object.assign(this.current, saved)
      this.saveMessage = '已保存'
      await this.loadTree()
      this.expanded = { ...this.expanded, [saved.vendor || '未分类']: true }
    },
    async removeCard() {
      if (!window.confirm(`删除「${this.current.name}」？`)) return
      await adminDeleteCard(this.current.id)
      this.current = null
      this.metrics = null
      await this.loadTree()
    },
    async saveMetrics() {
      this.metrics = await putCardMetrics(this.current.id, this.metrics)
      this.saveMessage = '索引已保存'
    },
    async saveAttr(attr) {
      const saved = await adminUpdateAttr(attr.id, {
        field_label: attr.field_label,
        field_value: attr.field_value,
        locked: true
      })
      Object.assign(attr, saved)
      this.saveMessage = '字段已保存'
    },
    async removeAttr(attr) {
      await adminDeleteAttr(attr.id)
      this.current.attrs = this.current.attrs.filter((item) => item.id !== attr.id)
    },
    async addAttr() {
      if (!this.newAttr.field_label.trim()) return
      const created = await adminAddAttr(this.current.id, this.newAttr)
      this.current.attrs.push(created)
      this.newAttr = { field_label: '', field_value: '' }
    },
    async savePrice(price) {
      try {
        const saved = await adminUpdatePrice(price.id, {
          variant_name: price.variant_name,
          msrp: price.msrp,
          market_price: price.market_price,
          secondhand_price: price.secondhand_price || '',
          currency: price.currency,
          price_month: price.price_month || currentMonth(),
          observed_at: fromLocalDatetime(price.observed_at_local),
          source_label: price.source_label || '',
          source_url: price.source_url || '',
          note: price.note || ''
        })
        Object.assign(price, saved)
        price.observed_at_local = toLocalDatetime(saved.observed_at)
        this.saveMessage = `价格已保存（${saved.price_month}）`
      } catch (err) {
        this.listError = err.response?.data?.error || '价格保存失败'
      }
    },
    async removePrice(price) {
      await adminDeletePrice(price.id)
      this.current.prices = this.current.prices.filter((item) => item.id !== price.id)
    },
    async addPrice() {
      try {
        const payload = {
          variant_name: this.newPrice.variant_name,
          msrp: this.newPrice.msrp,
          market_price: this.newPrice.market_price,
          secondhand_price: this.newPrice.secondhand_price || '',
          currency: this.newPrice.currency || 'USD',
          price_month: this.newPrice.price_month || currentMonth(),
          observed_at: fromLocalDatetime(this.newPrice.observed_at_local),
          source_label: this.newPrice.source_label || '人工录入',
          source_url: this.newPrice.source_url || ''
        }
        const created = hydratePrices({ prices: [await adminAddPrice(this.current.id, payload)] }).prices[0]
        const idx = this.current.prices.findIndex((p) => p.id === created.id)
        if (idx >= 0) this.current.prices.splice(idx, 1, created)
        else this.current.prices.unshift(created)
        this.newPrice = emptyPriceDraft()
        this.saveMessage = `已写入 ${created.price_month} 价格点`
      } catch (err) {
        this.listError = err.response?.data?.error || '添加价格失败'
      }
    },
    async rebuildMetrics() {
      try {
        const result = await rebuildMetrics()
        this.saveMessage = `已重建 ${result.count} 张卡的规格索引`
      } catch (err) {
        this.listError = err.response?.data?.error || '重建失败'
      }
    },
    async reclassifyVendors() {
      if (!window.confirm('按规则重分类厂商？会把 others/空厂商合并为「未分类」，并重建规格索引。已手动锁定的卡默认不改。')) return
      this.listError = ''
      try {
        const result = await reclassifyVendorsApi({})
        this.saveMessage = `已重分类 ${result.changed} 张卡，索引 ${result.metrics_count} 条`
        this.current = null
        await this.loadTree()
      } catch (err) {
        this.listError = err.response?.data?.error || '重分类失败'
      }
    }
  }
}
</script>
